import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Mail, Phone, Send, Briefcase, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useForm } from '@formspree/react';

const Contact = () => {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({
    email: false,
    phone: false
  });
  
  // Formspree integration
  const [state, handleFormspreeSubmit] = useForm("xeoybapr");

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: false
      });
    }
  };

  const validateForm = () => {
    const newErrors = {
      email: false,
      phone: false
    };

    // Email validation only
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = true;
    }

    // Phone - accept any format (no validation)

    setErrors(newErrors);
    return !newErrors.email;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      toast.error(t.contact.validation.invalidEmail);
      return;
    }
    
    // Submit to Formspree
    handleFormspreeSubmit(e);
  };
  
  // Handle success state from Formspree
  useEffect(() => {
    if (state.succeeded) {
      toast.success(t.contact.successToast);
      setFormData({ name: '', email: '', phone: '', message: '' });
      setErrors({ email: false, phone: false });
    }
  }, [state.succeeded]);

  const handleInvalid = (e) => {
    e.preventDefault();
    const input = e.target;
    if (input.validity.valueMissing) {
      input.setCustomValidity(t.contact.validation.required);
    } else if (input.validity.typeMismatch && input.type === 'email') {
      input.setCustomValidity(t.contact.validation.invalidEmail);
    }
  };

  const handleInput = (e) => {
    e.target.setCustomValidity('');
  };

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="min-h-screen pt-20 md:pt-32 pb-8 md:pb-12 px-6 bg-gray-50 relative overflow-hidden"
    >
      <div className="absolute top-0 right-0 w-1/2 h-full opacity-5 bg-gray-900" />

      <div className="max-w-5xl mx-auto relative z-10">
        <div className={`text-center mb-12 md:mb-16 transition-all duration-1000 transform ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2
            className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 md:mb-6 text-black"
            style={{ fontFamily: 'Crimson Text, serif' }}
          >
            {t.contact.title}
          </h2>
          <p className="text-base md:text-xl text-gray-700">
            {t.contact.description}
          </p>
        </div>

        {/* Contact Info - In one row */}
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-6 md:mb-8 transition-all duration-1000 transform ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <a href="mailto:info@apinternational.lv" className="group flex flex-col items-center justify-center text-center gap-4 p-8 md:p-10 bg-white rounded-xl border border-gray-100 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:border-gray-200">
            <div className="p-3 md:p-4 bg-black rounded-lg group-hover:scale-110 transition-transform duration-300">
              <Mail className="text-white" size={24} />
            </div>
            <div>
              <h3 className="font-semibold text-xl md:text-2xl mb-2 md:mb-3 text-black" style={{ fontFamily: 'Crimson Text, serif' }}>{t.contact.email}</h3>
              <p className="text-gray-700 text-sm md:text-base break-all">info@apinternational.lv</p>
            </div>
          </a>
          <a href="tel:+37129955249" className="group flex flex-col items-center justify-center text-center gap-4 p-8 md:p-10 bg-white rounded-xl border border-gray-100 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:border-gray-200">
            <div className="p-3 md:p-4 bg-black rounded-lg group-hover:scale-110 transition-transform duration-300">
              <Phone className="text-white" size={24} />
            </div>
            <div>
              <h3 className="font-semibold text-xl md:text-2xl mb-2 md:mb-3 text-black" style={{ fontFamily: 'Crimson Text, serif' }}>{t.contact.phone}</h3>
              <p className="text-gray-700 text-sm md:text-base">+371 29955249</p>
            </div>
          </a>
          <a href="https://wa.me/37129955249" target="_blank" rel="noopener noreferrer" className="group flex flex-col items-center justify-center text-center gap-4 p-8 md:p-10 bg-white rounded-xl border border-gray-100 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:border-gray-200">
            <div className="p-3 md:p-4 bg-black rounded-lg group-hover:scale-110 transition-transform duration-300">
              <MessageCircle className="text-white" size={24} />
            </div>
            <div>
              <h3 className="font-semibold text-xl md:text-2xl mb-2 md:mb-3 text-black" style={{ fontFamily: 'Crimson Text, serif' }}>{t.contact.whatsapp}</h3>
              <p className="text-gray-700 text-sm md:text-base">+371 29955249</p>
            </div>
          </a>
        </div>

        {/* Contact Form - Below */}
        <form
          onSubmit={handleSubmit}
          noValidate
          className={`max-w-5xl mx-auto transition-all duration-1000 transform ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
          style={{ transitionDelay: '200ms' }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-x-6 md:gap-y-6 mb-6">
            {/* Row 1 - Vārds and Tālrunis */}
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-900 text-center">
                {t.contact.name}
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onInvalid={handleInvalid}
                onInput={handleInput}
                placeholder={t.contact.namePlaceholder}
                required
                className="w-full px-4 py-4 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none transition-colors duration-200 bg-white text-center"
              />
            </div>
            
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-900 text-center">
                {t.contact.phone}
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                onInvalid={handleInvalid}
                onInput={handleInput}
                placeholder={t.contact.phonePlaceholder}
                className={`w-full px-4 py-4 rounded-lg border-2 ${errors.phone ? 'border-red-500' : 'border-gray-200'} focus:border-black focus:outline-none transition-colors duration-200 bg-white text-center`}
              />
            </div>
            
            {/* Row 2 - E-pasts and Ziņa */}
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-900 text-center">
                {t.contact.email}
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onInvalid={handleInvalid}
                onInput={handleInput}
                placeholder={t.contact.emailPlaceholder}
                required
                className={`w-full px-4 py-4 rounded-lg border-2 ${errors.email ? 'border-red-500' : 'border-gray-200'} focus:border-black focus:outline-none transition-colors duration-200 bg-white text-center`}
              />
            </div>
            
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-900 text-center">
                {t.contact.message}
              </label>
              <input
                type="text"
                name="message"
                value={formData.message}
                onChange={handleChange}
                onInvalid={handleInvalid}
                onInput={handleInput}
                placeholder={t.contact.messagePlaceholder}
                required
                className="w-full px-4 py-4 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none transition-colors duration-200 bg-white text-center"
              />
            </div>
          </div>
          <div className="flex justify-center mt-12 mb-12">
            <button
              type="submit"
              disabled={state.submitting}
              className={`flex items-center justify-center gap-2 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 border-2 border-black ${
                state.succeeded 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-red-600 hover:bg-red-700 hover:-translate-y-1'
              } text-white hover:shadow-xl ${state.submitting ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {state.succeeded ? t.contact.success : t.contact.send}
              {!state.succeeded && <Send size={20} />}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default Contact;